/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import isMobile from 'is-mobile-with-ipad';

import Grid from './Grid';
import Toast from './Toast';
import CircularButton from './CircularButton';
import Controller from './Controller';
import LoadingIndicator from './LoadingIndicator';

export default class HUD extends THREE.EventDispatcher {
  constructor(props) {
    super(props);
    this.setup(props);
  }

  setup = props => {
    this.setupProperties(props);

    this.setupCamera();
    this.setupScene();
    this.setupToasts();

    this.setupLoadingIndicator();
    this.setupController();
  };

  setupProperties = props => {
    this.renderer = props.renderer;
    this.canvas = props.canvas;
    this.duration = props.duration;
    this.debug = props.debug;

    this.buttonSize = 36 * this.renderer.getPixelRatio();
    this.buttonIconColor = new THREE.Color(0xffffff);
    this.buttonIconAlpha = 0.75;
    this.buttonBackgroundColor = new THREE.Color(0x222222);
    this.buttonBackgroundAlpha = 0.25;

    this.closeButtonIconColor = new THREE.Color(0xffffff);
    this.closeButtonIconAlpha = 1.0;
    this.closeButtonBackgroundColor = new THREE.Color(0x000000);
    this.closeButtonBackgroundAlpha = this.buttonBackgroundAlpha;

    this.toastTextColor = new THREE.Color(0xffffff);
    this.toastTextAlpha = 0.75;
    this.toastHeight = this.buttonSize * 1.5;
    this.toastBackgroundColor = this.closeButtonBackgroundColor;
    this.toastBackgroundAlpha = this.closeButtonBackgroundAlpha;
    this.topToasts = [];
    this.bottomToasts = [];

    this.spacing = props.spacing != undefined
      ? props.spacing
      : isMobile() ? 24 : 16;
    this.indicatorSize = 512;
  };

  setupToasts = () => {
    this.setupMoveDeviceToast();
    this.setupTapToPlaceToast();
    this.updateToasts();
  };

  setupMoveDeviceToast = () => {
    this.findingSurfaceToast = new Toast({
      text: 'finding a surface',
      height: this.toastHeight,
      textColor: this.toastTextColor,
      textAlpha: this.toastTextAlpha,
      backgroundColor: this.toastBackgroundColor,
      backgroundAlpha: this.toastBackgroundAlpha,
      font: this.font,
      alpha: 1.0,
    });
    this.findingSurfaceToast.setup();
    this.scene.add(this.findingSurfaceToast);
    this.topToasts.push(this.findingSurfaceToast);
  };

  setupTapToPlaceToast = () => {
    this.tapToPlaceToast = new Toast({
      text: 'tap to place',
      height: this.toastHeight,
      textColor: this.toastTextColor,
      textAlpha: this.toastTextAlpha,
      backgroundColor: this.toastBackgroundColor,
      backgroundAlpha: this.toastBackgroundAlpha,
      font: this.font,
      alpha: 1.0,
    });
    this.tapToPlaceToast.setup();
    this.scene.add(this.tapToPlaceToast);
    this.bottomToasts.push(this.tapToPlaceToast);
  };

  updateToasts = () => {
    let size = this.renderer.getDrawingBufferSize();

    for (let i = 0; i < this.bottomToasts.length; i++) {
      let toast = this.bottomToasts[i];
      let toastSize = toast.getSize();
      toast.position.set(
        size.width * 0.5,
        this.spacing + toastSize.height * 0.5,
        0
      );
      toast.updateMatrixWorld(true);
    }

    for (let i = 0; i < this.topToasts.length; i++) {
      let toast = this.topToasts[i];
      let toastSize = toast.getSize();
      toast.position.set(
        size.width * 0.5,
        size.height - (this.spacing + toastSize.height * 0.5),
        0
      );
      toast.updateMatrixWorld(true);
    }
  };

  dismissToasts = duration => {
    for (let i = 0; i < this.bottomToasts.length; i++) {
      this.bottomToasts[i].dismiss(duration);
    }
    for (let i = 0; i < this.topToasts.length; i++) {
      this.topToasts[i].dismiss(duration);
    }
  };

  setupCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.camera = new THREE.OrthographicCamera(
      0,
      size.width,
      size.height,
      0,
      -1,
      1
    );
  };

  setupScene = () => {
    this.scene = new THREE.Scene();
  };

  setupController = () => {
    this.controller = new Controller({
      canvas: this.canvas,
      camera: this.camera,
      scene: this.scene,
    });
  };

  updateCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    let width = size.width;
    let height = size.height;
    let aspect = width / height;
    this.camera.right = width;
    this.camera.top = height;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  };

  setupLoadingIndicator = () => {
    this.loadingIndicator = document.querySelector('#canvas-loader');
    this.updateLoadingIndicator();
    this.loadingIndicator.classList.add('show');
    this.loadingIndicator.classList.add('is-active');
  };

  updateLoadingIndicator = () => {
    let height = getComputedStyle(this.loadingIndicator.parentNode).height;
    height = parseInt(height);
    // Set --loader-height to a third of the container
    this.loadingIndicator.style.setProperty('--loader-height', height / 3);
  };

  update = time => {};

  render = () => {
    this.renderer.render(this.scene, this.camera);
  };

  resize = () => {
    this.updateCamera();
    this.updateLoadingIndicator();
    this.updateToasts();
  };

  hideLoadingIndicator = () => {
    this.loadingIndicator.classList.remove('show');
    this.loadingIndicator.classList.remove('is-active');
  };

  resetUserFlow = () => {
    this.userFlowPlaced = false;
    this.userFlowPlacedFirstTime = false;

    this.userFlowDragged = false;
    this.userFlowDraggedFirstTime = false;

    this.userFlowRotated = false;
    this.userFlowRotatedFirstTime = false;
  };

  enterAR = event => {
    this.resetUserFlow();
  };

  exitAR = event => {
    this.resetUserFlow();
    this.dismissToasts(this.duration);
  };

  down = event => {};

  up = event => {
    if (this.userFlowPlaced && !this.userFlowPlacedFirstTime) {
      this.tapToPlaceToast.dismiss(this.duration, 0);
      this.userFlowPlaced = false;
      this.userFlowPlacedFirstTime = true;
    }
  };

  modelPlaced = event => {
    this.userFlowPlaced = true;
  };

  modelDragged = event => {
    this.userFlowDragged = true;
  };

  modelRotated = event => {
    this.userFlowRotated = true;
  };

  findingSurface = event => {
    if (!this.userFlowPlaced && !this.userFlowPlacedFirstTime) {
      this.findingSurfaceToast.spawn(this.duration, 0);
    }
    this.tapToPlaceToast.dismiss(this.duration, 0);
    this.userFlowPlaced = false;
  };

  foundSurface = event => {
    this.findingSurfaceToast.dismiss(this.duration, 0);
    if (!this.userFlowPlaced && !this.userFlowPlacedFirstTime) {
      this.tapToPlaceToast.spawn(this.duration, 0);
    }
  };

  getSpacing = () => {
    return this.spacing;
  };

  setSpacing = spacing => {
    this.spacing = spacing;
  };
}



// WEBPACK FOOTER //
// ./src/hud/HUD.js